// Redux Form
import { SubmissionError } from "redux-form";

// Fetch request
import fetch from "../../core/fetch";

// Locale
import messages from "../../locale/messages";

// Redux Actions
import { getListingData } from "../../actions/getListing";
import { manageListingSteps } from "../../actions/manageListingSteps";
import { getListingFieldsValues } from "../../actions/getListingFieldsValues";
import { setLoaderStart, setLoaderComplete } from "../../actions/loader/loader";

// Redirect helper
import history from "../../core/history";

async function update(values, dispatch) {
  dispatch(setLoaderStart("updateListing"));

  // Prepare bedTypes as stringified JSON
  const bedTypes = JSON.stringify(values.bedTypes);
  const { buildingSize, ...rest } = { ...values, bedTypes };

  // === GraphQL Queries ===
  const updateListingQuery = `query (
    $id: Int,
    $roomType: String,
    $houseType: String,
    $residenceType: String,
    $bedrooms: String,
    $beds: Int,
    $personCapacity: Int,
    $bathrooms: Float,
    $bathroomType: String,
    $country: String,
    $street: String,
    $buildingName: String,
    $city: String,
    $state: String,
    $zipcode: String,
    $lat: Float,
    $lng: Float,
    $isMapTouched: Boolean,
    $amenities: [Int],
    $safetyAmenities: [Int],
    $spaces: [Int],
    $bedTypes: String,
    $checkinInfo: String,
  ) {
    updateListing (
      id: $id,
      roomType: $roomType,
      houseType: $houseType,
      residenceType: $residenceType,
      bedrooms: $bedrooms,
      beds: $beds,
      personCapacity: $personCapacity,
      bathrooms: $bathrooms,
      bathroomType: $bathroomType,
      country: $country,
      street: $street,
      buildingName: $buildingName,
      city: $city,
      state: $state,
      zipcode: $zipcode,
      lat: $lat,
      lng: $lng,
      isMapTouched: $isMapTouched,
      amenities: $amenities,
      safetyAmenities: $safetyAmenities,
      spaces: $spaces,
      bedTypes: $bedTypes,
      checkinInfo: $checkinInfo,
    ) {
      status
    }
  }`;

    const dogFriendlyMutation = `mutation (
      $listId: Int!,
      $fencingHeight: Int,
      $fencingDescription: String,
      $secureDogAreaType: Int,
      $gatesType: Int,
      $gatesDescription: String,
      $sizeOfDog: Int,
      $numberOfDogs: Int,
      $dogBed: Int,
      $doggyDoorGap: Int,
      $fenceCondition: Int,
      $fenceType: String
    ) {
      updateDogFriendlyAmenities (
        listId: $listId,
        fencingHeight: $fencingHeight,
        fencingDescription: $fencingDescription,
        secureDogAreaType: $secureDogAreaType,
        gatesType: $gatesType,
        gatesDescription: $gatesDescription,
        sizeOfDog: $sizeOfDog,
        numberOfDogs: $numberOfDogs,
        dogBed: $dogBed,
        doggyDoorGap: $doggyDoorGap,
        fenceCondition: $fenceCondition,
        fenceType: $fenceType
      ) {
        status
        errorMessage
      }
    }`;
  const faqMutation = `mutation ($listingId: Int!, $faqs: [FAQInput!]!) {
    upsertFAQs(listingId: $listingId, faqs: $faqs) {
      status
      errorMessage
    }
  }`;

  // === Step 1: Update Listing ===
  const listingResp = await fetch("/graphql", {
    method: "post",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: updateListingQuery,
      variables: {
        ...rest,
        roomType: String(rest.roomType),
        houseType: String(rest.houseType),
        bedrooms: String(rest.bedrooms),
        bathroomType: String(rest.bathroomType),
      },
    }),
    credentials: "include",
  });

  const { data } = await listingResp.json();

  if (data.updateListing.status !== "success") {
    dispatch(setLoaderComplete("updateListing"));
    if (data.updateListing.status === "notLoggedIn") {
      throw new SubmissionError({ _error: messages.notLoggedIn });
    }
    throw new SubmissionError({ _error: messages.somethingWentWrong });
  }

  // === Step 2: Update Dog-Friendly Amenities ===
  const dogFriendlyResp = await fetch("/graphql", {
    method: "post",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: dogFriendlyMutation,
      variables: {
        listId: rest.id,

        // always parse as integers if Yes, else set to 0 or null
        // fencingHeight: parseInt(rest.fencingHeight) || 0,
        fencingHeight: String(rest.fencingHeight) === "0" ? 1 : parseInt(rest.fencingHeight) || 0,


        fencingDescription: String(rest.fencingHeight) === "7" 
          ? rest.fencingDescription || "" 
          : "",

        secureDogAreaType: rest.secureDogArea === "yes"
          ? parseInt(rest.secureDogAreaType) || 0
          : 0,

        gatesType: rest.gates === "yes"
          ? parseInt(rest.gatesType) || 0
          : 0,

        gatesDescription: String(rest.gatesType) === "8"
          ? rest.gatesDescription || ""
          : "",
          
        sizeOfDog: String(rest.sizeOfDog) === "0" ? 1 : parseInt(rest.sizeOfDog) || 0,

        numberOfDogs: String(rest.numberOfDogs) === "0" ? 1 : parseInt(rest.numberOfDogs) || 0,

        dogBed: rest.dogBeds === "yes"
          ? parseInt(rest.dogBed) || 0
          : 0,

        doggyDoorGap: rest.doggyDoor === "yes"
          ? parseInt(rest.doggyDoorGap) || 0
          : 0,

        fenceCondition: String(rest.fenceCondition) === "0" ? 1 : parseInt(rest.fenceCondition) || 0,

        fenceType: String(rest.fenceCondition) === "6"
          ? rest.fenceType || ""
          : "",

      }



    }),
    credentials: "include",
  });

  const { data: dogData } = await dogFriendlyResp.json();

  if (dogData.updateDogFriendlyAmenities.status !== "success") {
    dispatch(setLoaderComplete("updateListing"));
    throw new SubmissionError({
      _error:
        dogData.updateDogFriendlyAmenities.errorMessage ||
        messages.somethingWentWrong,
    });
  }

  // === Step 3: Upsert FAQs ===
  if (Array.isArray(values.faqs) && values.faqs.length > 0) {
    const faqPayload = values.faqs
      .map((faq, index) => {
        const isEmpty = !faq.title?.trim() && !faq.content?.trim();

        // CASE 1: If it's an existing FAQ and now empty → mark as deleted
        if (faq.id && isEmpty) {
          return {
            id: faq.id,
            question: "",
            answer: "",
            sortOrder: index + 1,
            isDeleted: true,
          };
        }

        // CASE 2: If it's a new FAQ but empty → skip
        if (!faq.id && isEmpty) {
          return null;
        }

        // CASE 3: Valid FAQ → save/update
        return {
          id: faq.id || null,
          question: faq.title.trim(),
          answer: faq.content.trim(),
          sortOrder: index + 1,
          isDeleted: false,
        };
      })
      .filter(Boolean);
    const faqResp = await fetch("/graphql", {
      method: "post",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: faqMutation,
        variables: {
          listingId: values.id,
          faqs: faqPayload,
        },
      }),
      credentials: "include",
    });

    const { data: faqData } = await faqResp.json();

    if (faqData.upsertFAQs.status !== "success") {
      dispatch(setLoaderComplete("updateListing"));
      throw new SubmissionError({
        _error: faqData.upsertFAQs.errorMessage || messages.somethingWentWrong,
      });
    }
  }

  // === Final Redirect + Loaders ===
  await dispatch(manageListingSteps(values.id, 1));
  history.push(`/become-a-host/${values.id}/home`);
  await dispatch(getListingData(values.id));
  await dispatch(getListingFieldsValues("2", values.id));
  await dispatch(setLoaderComplete("updateListing"));
}

export default update;
